import { useState } from 'react';
import { Badge, Button, Card, EmptyState, ErrorState, Spinner, cx } from '@/components/ui';
import { formatKrwPlain, formatUsdPricePlain } from '@/lib/format';
import { isUsMarket, quoteBasisTime, quoteColorClass, resolveQuote } from './priceDisplay';
import { QuoteCell, QuoteHeaderNote, StockIdentity } from './components';
import { formatVolume } from './formatVolume';
import { summarizeAlerts, type AlertSummary } from './alertSummary';
import {
  MAX_WATCHLIST,
  type Price,
  type PriceAlert,
  type WatchlistItem,
} from './stockTypes';
import {
  usePriceAlerts,
  usePrices,
  useWatchlist,
  useWatchlistCategories,
  useWatchlistCategoryItemMutations,
  useWatchlistMutations,
} from './useStocks';
import { WatchlistCategoryModal } from './WatchlistCategoryModal';
import {
  ALL_TAB,
  buildWatchlistTabs,
  filterByTab,
  moveItem,
  resolveTab,
  type WatchlistTabKey,
} from './watchlistGroup';

/**
 * 관심종목 목록. (16.3 · 16.5 · 3.10)
 *
 * 🔴 두 종류의 가격이 섞이지 않게 한다
 *   목록 API 가 주는 lastClose 는 마지막 일봉의 종가이고,
 *   시세 API 가 주는 lastPrice 는 지금 값이다. 장중에는 다르다.
 *   시세가 오면 그것을 쓰고, 못 받았으면 종가임을 명시한다.
 *
 * 🔴 시세는 탭과 무관하게 전 종목을 대상으로 묻는다
 *   탭을 옮길 때마다 대상이 바뀌면 질의 키가 달라져 매번 새로 받는다.
 *   실제로 서버가 하는 일은 Redis 읽기 한 번이고, 어차피 배치가 합집합을
 *   통째로 채워 둔다. 걸러내는 것은 화면에서 한다.
 *
 * 순서 변경은 편집 모드로 한다 (3.10)
 *   드래그를 쓰지 않는다. HTML5 drag & drop 은 터치에서 아예 동작하지 않고,
 *   라이브러리를 넣으면 폰과 PC 에서 서로 다른 코드 경로를 유지하게 된다.
 *   위·아래 버튼은 두 기기에서 완전히 같고 키보드로도 쓸 수 있다.
 *   (docs/impl/54-watchlist-category.md)
 */

interface Props {
  onSelect: (stockId: number) => void;
  /** 국내/해외 탭이 정한다 (2026-08-20). 생략하면 국내다 */
  country?: 'KR' | 'US';
}

export function WatchlistPanel({ onSelect, country = 'KR' }: Props) {
  const watchlist = useWatchlist();
  const categories = useWatchlistCategories();
  const { reorder } = useWatchlistMutations();
  const { replaceItems } = useWatchlistCategoryItemMutations();

  const [tab, setTab] = useState<WatchlistTabKey>(ALL_TAB);
  const [managing, setManaging] = useState(false);

  /*
    편집 중인 목록. null 이면 편집 모드가 아니다.

    화면에 보이는 것과 따로 들고 있는 이유
      위·아래로 옮기고 빼는 동안 서버에 보내지 않는다. 저장을 눌러야 확정된다.
      매번 보내면 되돌릴 수 없고, 한 칸 옮길 때마다 요청이 나간다.
  */
  const [draft, setDraft] = useState<WatchlistItem[] | null>(null);

  /*
    국내/해외 탭으로 가른다 (2026-08-20).
    관심종목 표는 하나고 화면에서만 나눈다 - 상한 50개도 합산 그대로다.
    순서 저장(reorder)은 전체 목록 기준이라 탭별 편집이 전체 순서를 깨지 않게
    편집 모드는 걸러진 목록 위에서만 돈다 (카테고리 편집과 같은 방식).
  */
  const items = (watchlist.data ?? []).filter(
    (item) => isUsMarket(item.market) === (country === 'US'),
  );

  const prices = usePrices(items.map((item) => item.stockId));
  const priceByStock = new Map<number, Price>(
    (prices.data ?? []).map((price) => [price.stockId, price]),
  );

  /*
    가격 알림. 줄에 표식만 찍고 설정은 종목 상세 모달에서 한다.

    🔴 종목당 배열이다 (2026-09-03, V123)
      한 종목에 5개까지 걸리면서 Map<stockId, PriceAlert> 가 깨졌다.
      마지막 하나만 남고 나머지가 조용히 사라졌었다.
  */
  const alerts = usePriceAlerts();
  const alertsByStock = new Map<number, PriceAlert[]>();
  for (const alert of alerts.data ?? []) {
    const list = alertsByStock.get(alert.stockId);
    if (list) {
      list.push(alert);
    } else {
      alertsByStock.set(alert.stockId, [alert]);
    }
  }

  const tabs = buildWatchlistTabs(items, categories.data ?? []);

  /*
    🔴 고른 탭이 아직 살아 있는지 매 렌더마다 확인한다.

    상태를 useEffect 로 되돌리지 않는 이유
      되돌리는 렌더가 한 번 지나간다. 그 사이에 "어느 탭도 활성이 아니고
      목록은 0건" 인 화면이 스친다. 계산으로 풀면 그 순간 자체가 없다.
  */
  const activeTab = resolveTab(tabs, tab);
  const shown = draft ?? filterByTab(items, activeTab);

  /** 카테고리 탭에서만 '빼기' 를 준다. 전체·미분류에서는 뺄 대상이 모호하다 */
  const categoryTab = typeof activeTab === 'number' ? activeTab : null;

  /*
    🔴 미분류 탭에서는 순서를 못 바꾼다.

    미분류는 담는 곳이 아니라 전체를 거른 화면이다. 자기 순서를 갖고 있지 않다.
    여기서 저장하면 미분류 종목만 0,1,2... 로 다시 매겨지고 분류된 종목의 값은
    그대로 남아, 전체 탭의 줄 순서가 뒤죽박죽이 된다.
    미분류의 순서를 바꾸고 싶으면 전체 탭에서 바꾸면 그대로 따라온다.

    🔴 해외 탭에서도 못 바꾼다 (2026-08-20)
      reorder API 가 전체 목록 순서를 다시 매기는데, 해외 탭은 국내 종목이
      걸러진 부분 목록이라 그대로 보내면 국내 종목의 순서가 깨진다.
      해외 종목의 순서 변경이 필요해지면 그때 국가별 reorder 를 만든다.
  */
  const editable = (activeTab === ALL_TAB || categoryTab !== null) && country === 'KR';

  function 편집시작() {
    setDraft(filterByTab(items, activeTab));
  }

  function 저장() {
    if (draft === null) {
      return;
    }
    const stockIds = draft.map((item) => item.stockId);

    if (categoryTab !== null) {
      replaceItems.mutate({ categoryId: categoryTab, stockIds });
    } else {
      // 전체 탭은 관심종목 자체의 순서다. 미분류에서는 편집을 열어주지 않는다.
      reorder.mutate(stockIds);
    }
    setDraft(null);
  }

  if (watchlist.isLoading) {
    return (
      <Card title="관심종목">
        <Spinner />
      </Card>
    );
  }

  if (watchlist.isError) {
    return (
      <Card title="관심종목">
        <ErrorState
          message={watchlist.error?.message ?? '관심종목을 불러오지 못했습니다.'}
          onRetry={() => void watchlist.refetch()}
        />
      </Card>
    );
  }

  if (items.length === 0) {
    // 카테고리는 담은 것이 있어야 의미가 있다. 여기서는 관리 입구도 보여주지 않는다.
    return (
      <Card title="관심종목">
        <EmptyState
          title="담아둔 종목이 없습니다"
          description="위 검색창에서 종목을 찾아 담아두면 여기서 시세를 함께 볼 수 있습니다."
        />
      </Card>
    );
  }

  // 장 상태는 종목마다 같으므로 아무 하나에서 읽으면 된다.
  const stale = prices.data?.[0]?.stale ?? true;

  /*
    시세 기준 시각. (2026-08-19)

    🔴 화면 숫자는 지금 값이 아니다
      백엔드 배치 15초 → Redis 캐시 25초 → 프론트 폴링 15초 (2026-09-16 에 당김).
      최악 30초 남짓, 보통 15초 안팎 된 값이다.
      "실시간" 이라고만 적혀 있으면 지금 값으로 읽는다.
  */
  const basisTime = quoteBasisTime(prices.data);
  const editing = draft !== null;
  const saving = reorder.isPending || replaceItems.isPending;

  return (
    <Card
      title="관심종목"
      action={
        <span className="text-xs text-fg-subtle">
          {items.length} / {MAX_WATCHLIST}
          {' · '}
          {stale ? '장 마감' : '실시간'}
          {/*
            기준 시각. 장 마감 뒤에는 붙이지 않는다 -
            종가가 고정이라 몇 시 기준인지가 의미를 갖지 않고,
            15분 캐시 때문에 오히려 낡은 시각이 찍힌다.
          */}
          {!stale && basisTime && (
            <>
              {' · '}
              <span className="tabular">{basisTime}</span> 기준
            </>
          )}
        </span>
      }
    >
      {/*
        ── 탭 ── (2026-09-10 가로 스크롤로 바꿨다)

        🔴 왜 줄바꿈(flex-wrap)을 버렸나
          카테고리 상한이 3개였을 때는 [전체][A][B][C][미분류] 다섯이
          폰 375px 한 줄에 들어갔다. 그 상한을 없애면서 전제가 깨졌다 -
          열 개를 만들면 wrap 이 세 줄 네 줄이 되어 목록을 아래로 밀어낸다.

          증권사 앱들이 쓰는 방식으로 바꾼다. 탭은 옆으로 흐르고,
          [수정]·[카테고리 관리]는 오른쪽에 고정한다 - 그 둘까지 함께
          흘러가면 만들려고 누르는 버튼을 찾으러 스크롤해야 한다.

        🔴 스크롤바를 숨기지 않았다
          숨기면 더 있다는 것을 알 방법이 폰에서 사라진다.
          얇게 두고 pb-1 로 탭과 겹치지 않게만 했다.
      */}
      <div className="mb-2 flex items-start gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto pb-1">
          {tabs.map((option) => (
            <button
              key={String(option.key)}
              type="button"
              onClick={() => setTab(option.key)}
              // 편집 중에 탭을 옮기면 고치던 것이 조용히 사라진다.
              disabled={editing}
              className={cx(
                'shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium disabled:opacity-40',
                option.key === activeTab
                  ? 'bg-brand text-brand-fg'
                  : 'bg-surface-hover text-fg-muted',
              )}
            >
              {option.label}
              <span className="ml-1 tabular opacity-70">{option.count}</span>
            </button>
          ))}
        </div>

        {!editing && (
          <div className="flex shrink-0 items-center gap-1">
            {editable && (
              <button
                type="button"
                onClick={편집시작}
                className="rounded-full px-2 py-1 text-xs text-fg-subtle hover:bg-surface-hover"
              >
                수정
              </button>
            )}
            <button
              type="button"
              onClick={() => setManaging(true)}
              className="rounded-full px-2 py-1 text-xs text-fg-subtle hover:bg-surface-hover"
            >
              {tabs.length === 0 ? '＋ 카테고리' : '카테고리 관리'}
            </button>
          </div>
        )}
      </div>

      {editing && (
        <p className="mb-2 rounded-lg bg-surface-muted px-3 py-2 text-xs text-fg-muted">
          {categoryTab === null
            ? '순서를 바꾼 뒤 저장을 누르세요.'
            : '순서를 바꾸거나 이 카테고리에서 뺄 수 있습니다. 관심종목에서 지워지지는 않습니다.'}
        </p>
      )}

      {shown.length === 0 ? (
        // 카테고리를 막 만들었을 때의 정상 상태다. 오류가 아니다.
        <p className="py-6 text-center text-sm text-fg-muted">
          {editing
            ? '전부 뺐습니다. 저장하면 이 카테고리가 비워집니다.'
            : '이 카테고리에 담긴 종목이 없습니다. 종목을 열어 카테고리를 지정하세요.'}
        </p>
      ) : (
        <>
          {/*
            열 제목. (2026-09-03 사용자 요청 - 증권사 관심종목 화면처럼)

            🔴 표(table)가 아니라 목록(ul)이라 제목을 손으로 그린다
              행 전체가 버튼이라야 폰에서 누르기 쉬운데, tr 안에 button 을 넣으면
              그 구조가 무너진다. 대신 아래 행과 폭을 맞춰 제목 줄만 따로 얹는다.
              편집 중에는 숨긴다 - 그때는 행 모양이 완전히 다르다.

            🔴 제목은 가운데 정렬이다 (Th 와 같은 규칙, 2026-09-03 사용자 확정)
          */}
          {!editing && (
            <div className="flex items-center gap-3 border-b border-line py-2 text-xs font-medium text-fg-subtle">
              <span className="min-w-0 flex-1 text-center">종목</span>
              <span className="w-24 shrink-0 text-center sm:w-28">
                현재가
                <QuoteHeaderNote stale={stale} withVolume />
              </span>
              {/*
                🔴 "·" 가 아니라 "/" 다 (2026-09-12 사용자 요청)
                  값이 두 줄로 쌓여 있으므로(QuoteCell stacked) 제목도 두 이름이
                  갈린 것으로 읽혀야 한다. 표를 쓰는 다른 화면(주도주·섹터)의
                  StackedSortTh 와 같은 표기다 - 같은 뜻을 화면마다 다르게 쓰지 않는다.
                  여기는 표가 아니라 div 목록이라 열 정렬이 없어 버튼으로 두지 않았다.
              */}
              <span className="w-24 shrink-0 text-center sm:w-28">대비 / 등락률</span>
            </div>
          )}

          <ul className="flex flex-col divide-y divide-line">
          {shown.map((item, index) => (
            <li key={item.watchlistId}>
              {editing ? (
                <EditRow
                  item={item}
                  first={index === 0}
                  last={index === shown.length - 1}
                  removable={categoryTab !== null}
                  onMove={(delta) =>
                    setDraft((current) =>
                      current === null ? current : moveItem(current, index, delta),
                    )
                  }
                  onRemove={() =>
                    setDraft((current) =>
                      current === null
                        ? current
                        : current.filter((entry) => entry.stockId !== item.stockId),
                    )
                  }
                />
              ) : (
                <WatchRow
                  item={item}
                  price={priceByStock.get(item.stockId)}
                  alerts={alertsByStock.get(item.stockId) ?? []}
                  onSelect={() => onSelect(item.stockId)}
                />
              )}
            </li>
          ))}
          </ul>
        </>
      )}

      {editing && (
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
            취소
          </Button>
          <Button variant="primary" onClick={저장} disabled={saving}>
            저장
          </Button>
        </div>
      )}

      {!editing && (
        <p className="mt-3 text-xs text-fg-subtle">
          등락은 직전 영업일 종가 대비입니다. 토스가 전일종가를 주지 않아 우리 일봉으로
          계산합니다. 현재가 아래 거래량도 같은 이유로 마지막 일봉의 값이며 실시간이
          아닙니다.
        </p>
      )}

      <WatchlistCategoryModal
        open={managing}
        onClose={() => setManaging(false)}
        categories={categories.data ?? []}
        items={items}
      />
    </Card>
  );
}

/**
 * 편집 모드의 한 줄. 가격을 보여주지 않는다.
 *
 * 순서를 바꾸는 동안 15초마다 숫자가 흔들리면 어디를 옮기고 있었는지 놓친다.
 * 지금 필요한 정보는 종목명과 위치뿐이다.
 */
function EditRow({
  item,
  first,
  last,
  removable,
  onMove,
  onRemove,
}: {
  item: WatchlistItem;
  first: boolean;
  last: boolean;
  removable: boolean;
  onMove: (delta: number) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2 py-2">
      <span className="min-w-0 flex-1 truncate text-sm text-fg">{item.name}</span>

      <button
        type="button"
        onClick={() => onMove(-1)}
        disabled={first}
        aria-label={`${item.name} 위로`}
        className="rounded px-2 py-1 text-sm text-fg-muted hover:bg-surface-hover disabled:opacity-30"
      >
        ▲
      </button>
      <button
        type="button"
        onClick={() => onMove(1)}
        disabled={last}
        aria-label={`${item.name} 아래로`}
        className="rounded px-2 py-1 text-sm text-fg-muted hover:bg-surface-hover disabled:opacity-30"
      >
        ▼
      </button>

      {removable && (
        /*
          🔴 문구를 '빼기' 로만 두지 않는다
            전체 탭에서는 관심종목 삭제로, 카테고리 탭에서는 분류 해제로 읽힌다.
            같은 버튼이 탭에 따라 다른 일을 하는 것이 가장 위험한 모호함이라
            애초에 카테고리 탭에서만 보여주고 문구에도 대상을 적는다.
        */
        <button
          type="button"
          onClick={onRemove}
          className="rounded px-2 py-1 text-xs text-fg-subtle hover:bg-surface-hover"
        >
          카테고리에서 빼기
        </button>
      )}
    </div>
  );
}

/**
 * 관심종목 한 줄. (2026-09-03 증권사 관심종목 화면 형태로 개정)
 *
 * 행 전체가 버튼이다. 폰에서 작은 글자를 정확히 누르게 하지 않는다.
 * (추천 목록에서 카드 전체를 링크로 둔 것과 같은 이유)
 *
 * 🔴 오른쪽 값이 세 개가 됐다 — 현재가 · 대비 · 등락률
 *   추천·섹터와 같은 QuoteCell 을 쓴다. 화면마다 시세를 따로 그리면
 *   색 규칙(상승 빨강 / 하락 파랑 / 마감 회색)이 금방 어긋난다.
 *
 * 🔴 왼쪽 아래는 메모가 아니라 거래소 + 종목코드다
 *   메모가 있으면 그 아래 한 줄 더 붙인다. 코드가 사라지면 같은 이름의
 *   다른 종목과 구분이 안 되고, 미국 종목이 섞이면 어느 시장인지 모른다.
 */
function WatchRow({
  item,
  price,
  alerts,
  onSelect,
}: {
  item: WatchlistItem;
  price: Price | undefined;
  /** 이 종목에 걸린 알림 전부. 최대 5개다 (V123) */
  alerts: PriceAlert[];
  onSelect: () => void;
}) {
  // 무엇을 보여줄지는 priceDisplay 가 정한다.
  // 등락 부호가 반대로 나오는 종류의 버그는 눈으로 안 잡혀 테스트로 지킨다.
  const view = resolveQuote(price, item.lastClose, item.prevClose);
  const summary = summarizeAlerts(alerts, view.price);

  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-start gap-3 py-2.5 text-left hover:bg-surface-muted"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-fg">{item.name}</span>
          {item.securityType && item.securityType !== 'STOCK' && (
            <Badge tone="info">{item.securityType}</Badge>
          )}
          <AlertMark summary={summary} us={isUsMarket(item.market)} />
        </div>

        {/* 거래소 + 종목코드. 시세가 거래소를 주고, 못 받았으면 관심종목의 값을 쓴다 */}
        <StockIdentity market={view.market ?? item.market} symbol={item.symbol} />

        {/*
          업종. (2026-09-10 사용자 요청)
          종목 하나가 둘 이상일 수 있어(효성 = 전력기기 + 철강화학) 나열하고,
          좁은 화면에서 종목명을 밀어내지 않게 truncate 한다.
          미분류면 그리지 않는다 - 관심종목의 상당수가 아직 미분류다.
        */}
        {item.sectors.length > 0 && (
          <p className="truncate text-xs text-fg-subtle">{item.sectors.join(' · ')}</p>
        )}

        {item.memo && <p className="truncate text-xs text-fg-subtle">{item.memo}</p>}
      </div>

      {/*
        🔴 현재가와 대비를 두 칸으로 가른다 (2026-09-03)
          위의 제목 줄과 폭을 맞춰야 어느 값이 어느 제목인지 이어진다.
          한 덩어리로 두면 제목은 둘인데 값은 하나라 눈이 따라가지 못한다.
      */}
      <div className="w-24 shrink-0 text-right sm:w-28">
        <span className={cx('tabular text-sm font-semibold', quoteColorClass(view))}>
          {view.price === null
            ? '-'
            : isUsMarket(item.market)
              ? formatUsdPricePlain(view.price)
              : formatKrwPlain(view.price)}
        </span>
        {/*
          거래량. 현재가 아래 둘째 줄이다 (2026-09-10 사용자 요청).

          🔴 실시간이 아니다. 토스 현재가 API 가 거래량을 주지 않아
            마지막 일봉의 값을 쓴다 - 등락률과 같은 사정이고 같은 방식이다.
            표 아래 안내 문구가 그것을 말한다.
        */}
        {item.lastVolume !== null && (
          <p className="tabular text-[11px] leading-tight text-fg-subtle">
            {formatVolume(item.lastVolume)}
          </p>
        )}
      </div>

      <div className="w-24 shrink-0 text-right sm:w-28">
        <QuoteCell view={view} stacked showPrice={false} />
      </div>
    </button>
  );
}

/**
 * 종목명 옆의 알림 표식. (2026-09-03 사용자 요청 · 같은 날 축약)
 *
 * 🔴 종만 그린다. 기준가는 안 적는다
 *   처음에는 "가장 가까운 기준가 + 나머지 개수" 로 만들었는데, 폰에서 보니
 *   그 숫자가 거래소 표시와 함께 종목명을 통째로 밀어냈다.
 *   목록에서 알아야 할 것은 "이 종목에 알림이 있다" 까지이고,
 *   얼마에 걸었는지는 눌러서 보면 된다. 종목명이 먼저다.
 *
 * 🔴 두 개 이상이면 x2 처럼 개수만 붙인다
 *   하나뿐이면 숫자가 없다 - x1 은 정보가 아니라 잡음이다.
 *
 * 🔴 전부 울려 꺼진 것은 🔕 다
 *   "걸어뒀다" 와 "이미 울렸다" 가 같은 모양이면 재설정할 때를 놓친다.
 *
 * 하나도 없으면 아무것도 그리지 않는다. 빈 자리를 남기면 종목명이 밀린다.
 * 얼마에 걸렸는지는 마우스를 올리면 뜬다(title) - 화면 자리는 안 먹는다.
 */
function AlertMark({ summary, us }: { summary: AlertSummary; us: boolean }) {
  if (summary.total === 0) {
    return null;
  }
  if (summary.allTriggered) {
    return (
      <span
        className="shrink-0 text-xs text-fg-subtle"
        title="걸어둔 알림이 전부 울려 꺼졌습니다"
      >
        🔕
      </span>
    );
  }

  const money = us ? formatUsdPricePlain : formatKrwPlain;
  const hint =
    summary.nearest === null
      ? `알림 ${summary.total}개`
      : summary.total === 1
        ? `${money(summary.nearest.targetPrice)} ${summary.nearest.directionLabel} 알림`
        : `알림 ${summary.total}개. 가장 가까운 것이 ${money(summary.nearest.targetPrice)}`;

  return (
    <span className="shrink-0 whitespace-nowrap text-xs text-fg-muted" title={hint}>
      🔔
      {summary.total > 1 && <span className="ml-0.5 tabular">x{summary.total}</span>}
    </span>
  );
}
